import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { FileText, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

const SellerInvoicesPage = ({ user, onLogout }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, [currentPage]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin-billing/seller-invoices?page=${currentPage}&size=20`);
      setInvoices(res.data.invoices || []);
      setTotalPages(res.data.total_pages || 1);
      setTotal(res.data.total || 0);
    } catch (error) {
      console.error("Failed to fetch invoices:", error);
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (invoice) => {
    setDownloadingId(invoice.invoice_number);
    try {
      const res = await axios.get(
        `${API}/admin-billing/seller-invoices/download?invoice_number=${encodeURIComponent(invoice.invoice_number)}`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const safeInv = invoice.invoice_number.replace(/\//g, "_");
      link.setAttribute("download", `invoice_${safeInv}_${invoice.month_label || ""}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download invoice");
    } finally {
      setDownloadingId(null);
    }
  };

  const monthNames = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2" data-testid="invoices-page-title">Invoices</h1>
          <p className="text-slate-600">View and download your billing invoices</p>
        </div>

        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" style={{ color: "#00A699" }} />
              Billing Invoices
            </CardTitle>
            {total > 0 && (
              <Badge variant="outline" className="text-slate-600">
                {total} invoice{total !== 1 ? "s" : ""}
              </Badge>
            )}
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="font-medium">No invoices yet</p>
                <p className="text-sm mt-1">Invoices will appear here once generated by the admin.</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="invoices-table">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Invoice No</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Period</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Invoice Date</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Orders</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Pre-Tax</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Total</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Download</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {invoices.map((inv) => (
                        <tr key={inv.invoice_number} className="hover:bg-slate-50" data-testid={`invoice-row-${inv.invoice_number}`}>
                          <td className="px-4 py-4 align-middle text-sm font-medium text-slate-900">{inv.invoice_number}</td>
                          <td className="px-4 py-4 align-middle">
                            {inv.scope === "b2c" ? (
                              <Badge className="bg-teal-50 text-teal-700 border-teal-200">B2C</Badge>
                            ) : (
                              <Badge variant="outline" className="border-slate-200 text-slate-600">B2B</Badge>
                            )}
                          </td>
                          <td className="px-4 py-4 align-middle text-sm text-slate-700">
                            {inv.month_label || `${monthNames[inv.month]} ${inv.year}`}
                          </td>
                          <td className="px-4 py-4 align-middle text-sm text-slate-700">{inv.invoice_date || "-"}</td>
                          <td className="px-4 py-4 align-middle text-sm text-slate-700 text-right">{inv.total_orders || 0}</td>
                          <td className="px-4 py-4 align-middle text-sm text-slate-700 text-right">
                            {inv.pre_tax != null ? `₹${Number(inv.pre_tax).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "-"}
                          </td>
                          <td className="px-4 py-4 align-middle text-sm font-medium text-slate-900 text-right">
                            {inv.total_charges != null ? `₹${Number(inv.total_charges).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : "-"}
                          </td>
                          <td className="px-4 py-4 align-middle text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownload(inv)}
                              disabled={downloadingId === inv.invoice_number}
                              data-testid={`download-invoice-${inv.invoice_number}`}
                              className="gap-1"
                            >
                              {downloadingId === inv.invoice_number ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4" />
                              )}
                              PDF
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6 pt-4 border-t">
                    <p className="text-sm text-slate-600">
                      Page {currentPage} of {totalPages} ({total} invoices)
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
                        Previous
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </SellerLayout>
  );
};

export default SellerInvoicesPage;
